import type { NarrationDriftReport } from "./narrationDrift";
import type {
  NarrationAbsentReason,
  NarrationTimeline,
} from "./narrationTimeline";
import type { SyncTimeline } from "./syncTimeline";

// ---------------------------------------------------------------------------
// directorViewDebug — field diagnostics for the Director View (DIR-015).
//
// The panel's chips summarise; these logs *prove*. Every narration resolution
// emits one compact `console.debug` line (invisible unless DevTools verbose is
// on, so it costs nothing day to day). Flipping the debug switch —
//
//     localStorage.setItem("echoflicks.director-view.debug", "on")
//
// — upgrades that to the full structured report, mirrors it onto
// `window.__ECHOFLICKS_DIRECTOR_VIEW__` (so `copy(JSON.stringify(...))` in the
// console captures it for a ticket), and turns on live line-transition logs:
// one line whenever the active narration line changes, with the video clock
// and the line's resolved window. That last stream is the desync verifier —
// if what you HEAR disagrees with the logged window, the resolution is wrong;
// if they agree but the picture is wrong, the render is.
//
// Deliberately console-based: this is an authed internal CRM, the data logged
// is already on screen, and AGENTS.md forbids new env-backed switches — the
// localStorage key mirrors the existing per-browser view preferences.
// ---------------------------------------------------------------------------

export const DIRECTOR_VIEW_DEBUG_KEY = "echoflicks.director-view.debug";
const TAG = "[director-view]";

declare global {
  interface Window {
    __ECHOFLICKS_DIRECTOR_VIEW__?: NarrationDebugReport;
  }
}

export function isDirectorViewDebugEnabled(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(DIRECTOR_VIEW_DEBUG_KEY) === "on";
  } catch {
    return false;
  }
}

export type NarrationDebugLine = {
  index: number;
  start: number;
  end: number;
  plannedStart: number | null;
  placementDelta: number | null;
  words: number;
  visualShift: number | null;
  coverage: number | null;
  severity: string | null;
  attributedScene: number | string | null;
  onScreenScene: number | string | null;
};

export type NarrationDebugReport = {
  workloadId: string | null;
  sceneTier: string | null;
  sceneConfidence: string | null;
  narrationTier: string | null;
  narrationConfidence: string | null;
  absentReason: NarrationAbsentReason | null;
  lineCount: number;
  droppedLineCount: number;
  posterOffsetSeconds: number | null;
  contentEndSeconds: number | null;
  warnings: string[];
  drift: {
    meanCoverage: number | null;
    maxLead: number | null;
    maxLag: number | null;
    orphaned: number;
    onScreenMismatches: number;
    rampDetected: boolean;
    severity: string | null;
    estimated: boolean | null;
  };
  lines: NarrationDebugLine[];
};

/**
 * Serialise one resolution into a plain object. Pure so it can be pinned in
 * node-mode tests; everything here is already user-visible state.
 */
export function buildNarrationDebugReport(args: {
  workloadId?: string | null;
  sync: SyncTimeline | null;
  narration: NarrationTimeline | null;
  absentReason: NarrationAbsentReason | null;
  drift: NarrationDriftReport | null;
}): NarrationDebugReport {
  const { sync, narration, drift } = args;
  const driftByLine = new Map(
    (drift?.lines ?? []).map((line) => [line.index, line]),
  );
  return {
    workloadId: args.workloadId ?? null,
    sceneTier: sync?.source ?? null,
    sceneConfidence: sync?.confidence ?? null,
    narrationTier: narration?.source ?? null,
    narrationConfidence: narration?.confidence ?? null,
    absentReason: args.absentReason,
    lineCount: narration?.lines.length ?? 0,
    droppedLineCount: narration?.droppedLineCount ?? 0,
    posterOffsetSeconds: sync?.posterOffsetSeconds ?? null,
    contentEndSeconds: sync?.contentEndSeconds ?? null,
    warnings: [...(sync?.warnings ?? []), ...(narration?.warnings ?? [])],
    drift: {
      meanCoverage: drift?.summary.meanCoverageRatio ?? null,
      maxLead: drift?.summary.maxLeadSeconds ?? null,
      maxLag: drift?.summary.maxLagSeconds ?? null,
      orphaned: drift?.summary.orphanedLineCount ?? 0,
      onScreenMismatches: drift?.summary.onScreenMismatchCount ?? 0,
      rampDetected: drift?.summary.rampDetected ?? false,
      severity: drift?.summary.severity ?? null,
      estimated: drift?.summary.estimated ?? null,
    },
    lines: (narration?.lines ?? []).map((line) => {
      const lineDrift = driftByLine.get(line.index);
      return {
        index: line.index,
        start: line.startSeconds,
        end: line.endSeconds,
        plannedStart: line.plannedStartSeconds,
        placementDelta: line.placementDeltaSeconds,
        words: line.words?.length ?? 0,
        visualShift: lineDrift?.visualShiftSeconds ?? null,
        coverage: lineDrift?.coverageRatio ?? null,
        severity: lineDrift?.severity ?? null,
        attributedScene: lineDrift?.attributedSceneId ?? null,
        onScreenScene: lineDrift?.onScreenSceneId ?? null,
      };
    }),
  };
}

/** One line that answers "what did the resolver decide" at a glance. */
export function formatNarrationDebugSummary(
  report: NarrationDebugReport,
): string {
  if (report.absentReason) {
    return `${TAG} narration absent (${report.absentReason}) workload=${report.workloadId ?? "?"}`;
  }
  const drift = report.drift;
  const parts = [
    `scenes=${report.sceneTier}/${report.sceneConfidence}`,
    `narration=${report.narrationTier}/${report.narrationConfidence}`,
    `lines=${report.lineCount}${report.droppedLineCount ? `(-${report.droppedLineCount})` : ""}`,
    `coverage=${drift.meanCoverage != null ? Math.round(drift.meanCoverage * 100) + "%" : "?"}`,
    `maxLead=${drift.maxLead != null ? drift.maxLead.toFixed(2) + "s" : "-"}`,
    `orphaned=${drift.orphaned}`,
    `ramp=${drift.rampDetected ? "YES" : "no"}`,
  ];
  if (report.warnings.length > 0) {
    parts.push(`warnings=[${report.warnings.join(",")}]`);
  }
  return `${TAG} resolved workload=${report.workloadId ?? "?"} ${parts.join(" ")}`;
}

/**
 * Log a resolution. Compact line always (debug level); full report + window
 * handle when the debug switch is on.
 */
export function logNarrationResolution(report: NarrationDebugReport): void {
  if (typeof console === "undefined") return;
  // This module IS the sanctioned console surface for the Director View —
  // debug level so it stays invisible unless DevTools verbose is on.
  // eslint-disable-next-line no-console
  console.debug(formatNarrationDebugSummary(report));
  if (isDirectorViewDebugEnabled()) {
    // eslint-disable-next-line no-console
    console.debug(`${TAG} full resolution report`, report);
    if (typeof window !== "undefined") {
      window.__ECHOFLICKS_DIRECTOR_VIEW__ = report;
    }
  }
}

/**
 * Namespaced event log for the audio pipeline and the live line tracker.
 * Always debug-level; `verboseOnly` events stay silent unless the switch is on
 * (line transitions fire every few seconds — signal when wanted, noise when
 * not).
 */
export function logDirectorViewEvent(
  event: string,
  detail: Record<string, unknown>,
  options?: { verboseOnly?: boolean },
): void {
  if (typeof console === "undefined") return;
  if (options?.verboseOnly && !isDirectorViewDebugEnabled()) return;
  // eslint-disable-next-line no-console
  console.debug(`${TAG} ${event}`, detail);
}
